import {
  countWellSkyClientStatuses,
  isMonitoredCareHubClient,
} from "./clientWellSkyStatus";
import { getClientAvatarUrl } from "./clientDisplay";
import { UNITY_FLEMINGTON_LOCATION_ID } from "./unityLocationScope";

export const TRIAGE_BUCKET_META = {
  all: {
    key: "all",
    title: "All Triage Cases",
    tone: "blue",
    subtitle:
      "Consolidated view of Red, Yellow, Green, and Gray triage cases. Each row keeps its triage color for quick reference.",
  },
  action: {
    key: "action",
    title: "Red Triage — Action Needed",
    tone: "red",
    subtitle:
      "Clients with significant decline in their Aging in Place Score or other high-risk changes requiring prompt follow-up.",
  },
  monitor: {
    key: "monitor",
    title: "Yellow Triage — Monitor",
    tone: "amber",
    subtitle:
      "Changes worth watching closely and reviewing soon.",
  },
  stable: {
    key: "stable",
    title: "Green Triage — Stable / Improved",
    tone: "green",
    subtitle:
      "Positive or stable changes with no urgent action needed.",
  },
  review: {
    key: "review",
    title: "Gray Triage — Review Data",
    tone: "gray",
    subtitle:
      "Missing, stale, conflicting, or insufficient information requiring review.",
  },
};

export function filterCareHubClients(allClients = []) {
  const counts = countWellSkyClientStatuses(allClients);
  if (counts.total <= 0) return allClients;
  return allClients.filter((c) => isMonitoredCareHubClient(c));
}

function normalizeId(raw) {
  if (raw == null) return "";
  if (typeof raw === "object") {
    return String(raw._id || raw.id || "").trim();
  }
  return String(raw).trim();
}

function clientKey(client) {
  return normalizeId(client?._id || client?.id || client?.clientId);
}

function clientAge(client) {
  if (client?.age || client?.clientAge) return client.age || client.clientAge;
  const dob = client?.dateOfBirth || client?.dob;
  if (!dob) return "";
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return "";
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
}

function clientShortId(id) {
  const s = String(id || "").trim();
  if (!s) return "";
  return `#${s.slice(-4).toUpperCase()}`;
}

function initials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function inferSource(note, bucket, noteSource) {
  const src = String(noteSource || "").toLowerCase();
  if (src === "assessment_notes") {
    return { label: "Nurse Assessment", type: "nurse" };
  }
  if (src === "pacsana" || src === "pacsana_device") {
    return { label: "PacSana", type: "device" };
  }
  if (src === "sensi" || src === "sensi_device") {
    return { label: "Sensi", type: "sensi" };
  }
  if (src === "history" || src === "category_delta" || src === "current_assessment") {
    return { label: "AIP assessment", type: "aip" };
  }
  const n = String(note || "").toLowerCase();
  if (n.includes("caregiver") || n.includes(" ai call") || n.includes("call assist")) {
    return { label: "Caregiver AI Call", type: "caregiver" };
  }
  if (n.includes("nurse") || n.includes("assessment")) {
    return { label: "Nurse Assessment", type: "nurse" };
  }
  if (n.includes("pacsana")) {
    return { label: "PacSana", type: "device" };
  }
  if (n.includes("sensi")) {
    return { label: "Sensi", type: "sensi" };
  }
  if (n.includes("device") || n.includes("sensor")) {
    return { label: "PacSana", type: "device" };
  }
  if (n.includes("able care") || n.includes("ablecare")) {
    return { label: "Able Care Device", type: "able" };
  }
  if (bucket === "review") return { label: "Data review", type: "review" };
  return { label: "AIP assessment", type: "aip" };
}

/**
 * Triage queue: only material AIP changes vs baseline — not the full active roster.
 * Dashboard / Change Center still list every client; triage is deltas only.
 */
export function rowHasTriageSignal(row) {
  const id = normalizeId(row?.clientId);
  if (!id) return false;
  if (row.change == null) return false;
  const ch = Number(row.change);
  if (!Number.isFinite(ch) || ch === 0) return false;
  const prev = Number(row.previousScore);
  // First score moving off 0 is onboarding, not a post-baseline condition change.
  if (Number.isFinite(prev) && prev === 0 && ch > 0) return false;
  return true;
}

function deriveWorkflowStatus(row) {
  if (row.workflowStatus) return row.workflowStatus;
  if (row.status === "overdue") return "overdue";
  if (row.status === "resolved") return "resolved";
  if (row.status === "in_progress") return "in_progress";
  if (row.status === "assigned") return "assigned";
  return "new";
}

function classifyScoreBand(currentScore) {
  const current = Number(currentScore);
  if (!Number.isFinite(current) || current <= 0) return "review";
  if (current < 40) return "action";
  if (current < 75) return "monitor";
  return "stable";
}

function classifyTriageBucket(change, currentScore) {
  const current = Number(currentScore);
  if (!Number.isFinite(current) || current <= 0) return "review";
  if (change == null) return classifyScoreBand(current);
  const ch = Number(change);
  if (ch === 0) return classifyScoreBand(current);
  if (ch <= -3 || (current > 0 && current < 40)) return "action";
  if (ch > 0) return "stable";
  if (ch < 0) return "monitor";
  return classifyScoreBand(current);
}

function dedupeLatestChanges(changeResults = []) {
  const map = new Map();
  changeResults.forEach((row) => {
    const id = normalizeId(row.clientId);
    if (!id) return;
    const prev = map.get(id);
    const t = new Date(row.changeDate || 0).getTime();
    const pt = prev ? new Date(prev.changeDate || 0).getTime() : -1;
    if (!prev || t >= pt) map.set(id, row);
  });
  return [...map.values()];
}

export function formatTriageDetected(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  if (isToday) return `Today, ${time}`;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function resolveClientName(client, row, id) {
  const fromRow = String(row?.clientName || "").trim();
  if (fromRow) return fromRow;
  const combined = [client?.firstName, client?.lastName].filter(Boolean).join(" ").trim();
  const candidates = [
    client?.name,
    client?.clientName,
    combined,
    client?.displayName,
  ].map((v) => String(v || "").trim()).filter(Boolean);
  if (candidates.length) return candidates[0];
  const short = clientShortId(id);
  return short ? `Client ${short}` : "Client";
}

export function computeSummaryFromCases(activeClientCount, cases = []) {
  const bucketStats = (bucket) => {
    const scoped = cases.filter((c) => c.bucket === bucket);
    const open = scoped.filter((c) => c.workflowStatus !== "resolved");
    const newCount = open.filter((c) => c.workflowStatus === "new").length;
    const assignedCount = open.filter(
      (c) =>
        c.workflowStatus === "assigned" || c.workflowStatus === "in_progress",
    ).length;
    const overdueCount = open.filter((c) => c.workflowStatus === "overdue").length;
    return {
      total: open.length,
      newCount,
      assignedCount,
      overdueCount,
    };
  };

  return {
    activeClients: activeClientCount,
    monitoredToday: activeClientCount,
    action: bucketStats("action"),
    monitor: bucketStats("monitor"),
    stable: { total: cases.filter((c) => c.bucket === "stable").length },
    reviewData: { total: cases.filter((c) => c.bucket === "review").length },
  };
}

/** @deprecated prefer computeSummaryFromCases after buildTriageCases */
export function computeCareTriageSummary(clients = [], changeResults = []) {
  const declines = changeResults.filter((r) => Number(r.change) < 0);
  const improvements = changeResults.filter((r) => Number(r.change) > 0);

  const reviewData = clients.filter(
    (c) => !c.aipScore || Number(c.aipScore) <= 0,
  );
  const monitor = clients.filter((c) => {
    const s = Number(c.aipScore);
    return s >= 40 && s < 75;
  });
  const stable = clients.filter((c) => Number(c.aipScore) >= 75);
  const lowScore = clients.filter((c) => {
    const s = Number(c.aipScore);
    return s > 0 && s < 40;
  });

  const actionIds = new Set([
    ...lowScore.map((c) => clientKey(c)).filter(Boolean),
    ...declines
      .filter((r) => Number(r.change) <= -3)
      .map((r) => String(r.clientId || "").trim())
      .filter(Boolean),
  ]);

  const actionNew = declines.filter((r) =>
    actionIds.has(String(r.clientId || "").trim()),
  ).length;
  const actionAssigned = Math.max(0, actionIds.size - actionNew);

  const monitorFromChanges = changeResults.filter((r) => {
    const id = String(r.clientId || "").trim();
    if (!id || actionIds.has(id)) return false;
    const ch = Number(r.change);
    return ch < 0 && ch > -3;
  }).length;

  return {
    activeClients: clients.length,
    monitoredToday: clients.length,
    action: {
      total: actionIds.size,
      newCount: actionNew,
      assignedCount: actionAssigned,
    },
    monitor: {
      total: monitor.length + monitorFromChanges,
      newCount: monitorFromChanges,
      assignedCount: Math.max(0, monitor.length),
    },
    stable: {
      total: stable.length + improvements.length,
    },
    reviewData: {
      total: reviewData.length,
    },
    actionClientIds: actionIds,
  };
}

export function mergeChangeResultsFromLocations(payloads = []) {
  const merged = [];
  payloads.forEach((payload) => {
    if (Array.isArray(payload?.results)) merged.push(...payload.results);
  });
  return dedupeLatestChanges(merged);
}

const UNITY_FLEMINGTON_TRIAGE_DEMO = {
  "tom|smith": {
    bucket: "action",
    source: "PacSana",
    sourceType: "device",
    noteSource: "pacsana",
    priority: "high",
  },
  "thomas|smith": {
    bucket: "action",
    source: "PacSana",
    sourceType: "device",
    noteSource: "pacsana",
    priority: "high",
  },
  "marty|goldberg": {
    bucket: "action",
    source: "Sensi",
    sourceType: "sensi",
    noteSource: "sensi",
    priority: "high",
  },
};

function personNameKey(client) {
  const fn = String(client?.firstName || "").trim().toLowerCase();
  const ln = String(client?.lastName || "").trim().toLowerCase();
  if (!fn || !ln) return "";
  return `${fn}|${ln}`;
}

function personNameKeyFromDisplayName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return "";
  return `${parts[0].toLowerCase()}|${parts[parts.length - 1].toLowerCase()}`;
}

function resolveDemoPersonKey(client, row) {
  return (
    personNameKey(client) ||
    personNameKeyFromDisplayName(row?.clientName) ||
    personNameKey({ firstName: row?.firstName, lastName: row?.lastName })
  );
}

function isUnityFlemingtonViewer(options = {}) {
  return normalizeId(options.activeLocationId) === UNITY_FLEMINGTON_LOCATION_ID;
}

function clientSiteLocationId(client) {
  return normalizeId(
    client?.locationId ||
      client?.location?._id ||
      client?.location?.id ||
      client?.location,
  );
}

/** Unity Flemington demo: Tom Smith (PacSana) + Marty Goldberg (Sensi) in Red triage. */
export function applyUnityFlemingtonTriageDemo(
  cases = [],
  clientById = new Map(),
  options = {},
) {
  const viewerFlemington = isUnityFlemingtonViewer(options);

  return cases.map((row) => {
    const client = clientById.get(row.clientId) || {};
    const atFlemingtonSite =
      clientSiteLocationId(client) === UNITY_FLEMINGTON_LOCATION_ID;
    if (!atFlemingtonSite && !viewerFlemington) return row;

    const demo = UNITY_FLEMINGTON_TRIAGE_DEMO[resolveDemoPersonKey(client, row)];
    if (!demo) return row;
    return {
      ...row,
      bucket: demo.bucket,
      source: demo.source,
      sourceType: demo.sourceType,
      noteSource: demo.noteSource ?? row.noteSource,
      priority: demo.priority ?? row.priority,
    };
  });
}

export function buildTriageCases(clients = [], changeResults = [], options = {}) {
  const byId = new Map();
  clients.forEach((c) => {
    const id = clientKey(c);
    if (id) byId.set(id, c);
  });

  const cases = [];

  dedupeLatestChanges(changeResults).forEach((row) => {
    const id = normalizeId(row.clientId);
    if (!id) return;
    if (!byId.has(id)) return;
    if (!rowHasTriageSignal(row)) return;

    const client = byId.get(id) || {};
    const change = row.change != null ? Number(row.change) : null;
    const currentScore =
      row.currentScore != null
        ? row.currentScore
        : client.aipScore != null
          ? client.aipScore
          : null;
    const bucket = classifyTriageBucket(change, currentScore);
    if (!bucket) return;

    const note = row.note || "";
    const source = inferSource(note, bucket, row.noteSource);
    const name = resolveClientName(client, row, id);

    cases.push({
      id: `${id}-${row.changeDate || "row"}`,
      clientId: id,
      clientName: name,
      clientInitials: initials(name),
      clientShortId: clientShortId(id),
      age: clientAge(client),
      aipPrevious: row.previousScore,
      aipCurrent: currentScore,
      change,
      note:
        note ||
        (bucket === "review"
          ? "Incomplete or missing AIP score data"
          : change == null || change === 0
            ? "Current AIP score (no change in selected range)"
            : "—"),
      noteSource: row.noteSource || null,
      changeDate: row.changeDate,
      bucket,
      status: row.status || "new",
      source: source.label,
      sourceType: source.type,
      priority:
        change != null && (change <= -5 || Number(currentScore) < 40) ? "high" : "normal",
      owner: row.owner || "",
      dueDate: row.dueDate || null,
      clientPhone: client.phone || client.phoneNumber || "",
      clientAddress: [client.address, client.city, client.state]
        .filter(Boolean)
        .join(", "),
      avatarUrl: getClientAvatarUrl(client),
      firstName: client.firstName,
      lastName: client.lastName,
    });
  });

  const withWorkflow = cases
    .map((row) => {
      const workflowStatus = deriveWorkflowStatus(row);
      const clientName = String(row.clientName || "").trim() || resolveClientName(
        byId.get(row.clientId) || {},
        row,
        row.clientId,
      );
      return {
        ...row,
        clientName,
        clientInitials: initials(clientName),
        workflowStatus,
        owner: row.owner || "",
        dueDate: row.dueDate || null,
      };
    })
    .filter((row) => row.clientId && row.clientName);

  return applyUnityFlemingtonTriageDemo(withWorkflow, byId, options);
}

const BUCKET_ALIASES = {
  action: "action",
  monitor: "monitor",
  stable: "stable",
  review: "review",
  reviewData: "review",
};

export function formatReviewStatus(reviewStatus) {
  if (!reviewStatus || reviewStatus === "none") return "—";
  if (reviewStatus === "ready_for_review") return "Ready for Review";
  if (reviewStatus === "follow_up_needed") return "Follow-Up Needed";
  return String(reviewStatus);
}

export function caseFromWorkflowRecord(wf) {
  const snap = wf.eventSnapshot || {};
  const name = snap.clientName || "Client";
  return {
    id: wf.eventKey,
    eventKey: wf.eventKey,
    clientId: wf.clientId,
    clientName: name,
    clientInitials: initials(name),
    clientShortId: clientShortId(wf.clientId),
    age: "",
    aipPrevious: snap.previousScore,
    aipCurrent: snap.currentScore,
    change: snap.change,
    note: snap.note || "—",
    changeDate: wf.detectedAt || wf.resolvedAt,
    bucket: wf.triageBucket || "action",
    source: snap.source || "AIP assessment",
    sourceType: "aip",
    priority: wf.priority || "normal",
    fromWorkflowArchive: true,
  };
}

export function applyWorkflowOverlay(cases = [], workflows = []) {
  const map = new Map();
  workflows.forEach((wf) => {
    if (wf?.eventKey) map.set(wf.eventKey, wf);
  });

  const merged = cases.map((row) => {
    const wf = map.get(row.id);
    if (!wf) return { ...row, eventKey: row.id };
    return {
      ...row,
      eventKey: wf.eventKey,
      workflowStatus: wf.workflowStatus,
      reviewStatus: wf.reviewStatus,
      reviewStatusLabel: formatReviewStatus(wf.reviewStatus),
      reviewResult: wf.reviewResult || "",
      owner: wf.owner || "",
      dueDate: wf.dueDate || null,
      priority: wf.priority || row.priority,
      notes: wf.notesPreview || wf.assignmentNotes || row.notes || "",
      actionType: wf.actionType || "",
      assignmentNotes: wf.assignmentNotes || "",
      messages: wf.messages || [],
      auditTrail: wf.auditTrail || [],
      resolutionSummary: wf.resolutionSummary || {},
      outcome: wf.outcome || "",
      resolvedAt: wf.resolvedAt || null,
      resolvedBy: wf.resolvedBy || "",
      unreadForSupervisor: wf.unreadForSupervisor,
      unreadForAssignee: wf.unreadForAssignee,
      status:
        wf.workflowStatus === "new"
          ? "new"
          : wf.workflowStatus === "in_progress"
            ? "in_progress"
            : wf.workflowStatus,
    };
  });

  const seen = new Set(merged.map((r) => r.id));
  workflows.forEach((wf) => {
    if (wf.workflowStatus !== "resolved") return;
    if (seen.has(wf.eventKey)) return;
    const archived = caseFromWorkflowRecord(wf);
    merged.push(
      applyWorkflowOverlay([archived], [wf])[0],
    );
  });

  return merged.map((row) => ({
    ...row,
    workflowStatus: row.workflowStatus || deriveWorkflowStatus(row),
    reviewStatusLabel: row.reviewStatusLabel || formatReviewStatus(row.reviewStatus),
  }));
}

export function filterTriageByBucket(cases, bucketKey) {
  if (!bucketKey || bucketKey === "all") return cases;
  const normalized = BUCKET_ALIASES[bucketKey || "action"] || "action";
  return cases.filter((c) => c.bucket === normalized);
}

export const TRIAGE_BUCKET_TONE = {
  action: "red",
  monitor: "amber",
  stable: "green",
  review: "gray",
};

export const TRIAGE_BUCKET_LABEL = {
  action: "Red — Action Needed",
  monitor: "Yellow — Monitor",
  stable: "Green — Stable / Improved",
  review: "Gray — Review Data",
};

export function filterTriageWorkflow(cases, tab = "new") {
  switch (tab) {
    case "assigned":
      return cases.filter((c) =>
        ["assigned", "in_progress"].includes(c.workflowStatus),
      );
    case "overdue":
      return cases.filter((c) => c.workflowStatus === "overdue");
    case "resolved":
      return cases.filter((c) => c.workflowStatus === "resolved");
    case "all":
      return cases.filter((c) => c.workflowStatus !== "resolved");
    case "new":
    default:
      return cases.filter((c) => c.workflowStatus === "new");
  }
}

/** @deprecated use filterTriageWorkflow */
export function filterTriageCases(cases, tab = "new") {
  const actionScoped = cases.filter((c) => c.bucket === "action");
  return filterTriageWorkflow(actionScoped, tab);
}

export function countTriageWorkflow(cases) {
  return {
    new: filterTriageWorkflow(cases, "new").length,
    assigned: filterTriageWorkflow(cases, "assigned").length,
    overdue: filterTriageWorkflow(cases, "overdue").length,
    resolved: filterTriageWorkflow(cases, "resolved").length,
  };
}

/** Open assignment work (any triage color) — for Assigned Cases nav */
export function filterActiveAssignmentCases(cases = []) {
  return cases.filter((c) =>
    ["assigned", "in_progress", "overdue"].includes(c.workflowStatus),
  );
}

export function countActiveAssignmentCases(cases = []) {
  const open = filterActiveAssignmentCases(cases);
  return {
    assigned: filterTriageWorkflow(open, "assigned").length,
    overdue: filterTriageWorkflow(open, "overdue").length,
    total: open.length,
  };
}

export const WORKFLOW_GROUPS = [
  { id: "new", label: "New" },
  { id: "assigned", label: "Assigned / In Progress" },
  { id: "overdue", label: "Overdue" },
];

/** Column sort config for the triage queue. `firstDir` is the direction a
 * column uses on its first click (dates newest first, score lowest first). */
export const TRIAGE_SORT_COLUMNS = {
  client: { label: "Client", firstDir: "asc", type: "text", get: (r) => r.clientName },
  score: { label: "AIP Score", firstDir: "asc", type: "number", get: (r) => r.aipCurrent },
  detected: { label: "Detected", firstDir: "desc", type: "date", get: (r) => r.changeDate },
  owner: { label: "Owner", firstDir: "asc", type: "text", get: (r) => r.owner },
  due: { label: "Due", firstDir: "asc", type: "date", get: (r) => r.dueDate },
  priority: {
    label: "Priority",
    firstDir: "desc",
    type: "number",
    get: (r) => ({ high: 3, normal: 2, low: 1 })[r.priority] ?? null,
  },
  resolvedAt: { label: "Resolved At", firstDir: "desc", type: "date", get: (r) => r.resolvedAt },
};

function sortValue(col, row) {
  const raw = col.get(row);
  if (raw == null || raw === "" || raw === "—") return null;
  if (col.type === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (col.type === "date") {
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? null : t;
  }
  return String(raw).toLowerCase();
}

/** Sorts rows by a TRIAGE_SORT_COLUMNS key; empty values always sink to the bottom. */
export function sortTriageRows(rows = [], key = "detected", dir = "desc") {
  const col = TRIAGE_SORT_COLUMNS[key] || TRIAGE_SORT_COLUMNS.detected;
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = sortValue(col, a);
    const vb = sortValue(col, b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const cmp = typeof va === "string" ? va.localeCompare(vb) : va - vb;
    return cmp * sign;
  });
}
